import { DateTime } from 'luxon';
import { observer } from 'mobx-react';
import * as React from 'react';
import { DateInput, FormCell, Grid, IDateInputValue, ISelectOption, NumberInput, Select, TextInput } from 'ui-toolkit';
import { MortgageDocumentType } from '../classes/MortgageDocumentType';
import { IRecordedMortgageRequired, IRecordedMortgage } from '../classes/RecordedMortgage';
import getOptionFromSnakeCase from '../utils/getOptionFromSnakeCase';
import getOptionsListFromSnakeCase from '../utils/getOptionsListFromSnakeCase';
import { TimeInput } from './fields/TimeInput/TimeInput';

interface IRecordedMortgageEditProps {
  data: IRecordedMortgage;
  requiredFields: IRecordedMortgageRequired;
  errorRecordedMortgage: any;
  onRecordedMortgageChange: (value: IRecordedMortgage, propName: string, error?: string | null) => void;
}

class RecordedMortgageEdit extends React.Component<IRecordedMortgageEditProps> {
  public render() {
    return (
      <>
        <Grid columnCount={2}>
          <TextInput
            label={'Mortgagor name'}
            placeholder={'Channing Tatum'}
            isRequired={this.props.requiredFields.mortgagor_name}
            errorText={this.props.errorRecordedMortgage.mortgagor_name}
            value={this.props.data.mortgagor_name}
            onChange={this.onChange.bind(this, 'mortgagor_name')}
          />
          <TextInput
            label={'Lender name'}
            placeholder={'Central Loans'}
            isRequired={this.props.requiredFields.lender_name}
            errorText={this.props.errorRecordedMortgage.lender_name}
            value={this.props.data.lender_name}
            onChange={this.onChange.bind(this, 'lender_name')}
          />
        </Grid>
        <Grid columnCount={this.props.requiredFields.other_document_type ? 3 : 2}>
          <TextInput
            label={'Trustee name'}
            placeholder={'Alyssa Milano'}
            isRequired={this.props.requiredFields.trustee_name}
            errorText={this.props.errorRecordedMortgage.trustee_name}
            value={this.props.data.trustee_name}
            onChange={this.onChange.bind(this, 'trustee_name')}
          />
          <FormCell
            label={'Document type'}
            isRequired={this.props.requiredFields.document_type}
            errorText={this.props.errorRecordedMortgage.document_type}
          >
            <Select
              onChange={this.onSelect.bind(this, 'document_type')}
              value={getOptionFromSnakeCase(this.props.data.document_type)}
              options={getOptionsListFromSnakeCase(Object.keys(MortgageDocumentType))}
            />
          </FormCell>
          {this.props.data.document_type === MortgageDocumentType.other ? (
            <TextInput
              label={'Other Document type'}
              isRequired={this.props.requiredFields.other_document_type}
              errorText={this.props.errorRecordedMortgage.other_document_type}
              value={this.props.data.other_document_type}
              onChange={this.onChange.bind(this, 'other_document_type')}
            />
          ) : null}
        </Grid>
        <Grid columnCount={2}>
          <NumberInput
            label={'Original Loan amount / Credit limit'}
            isRequired={this.props.requiredFields.original_loan_amount}
            errorText={this.props.errorRecordedMortgage.original_loan_amount}
            value={this.props.data.original_loan_amount}
            mode={'currency'}
            prefix={'$'}
            placeholder={'128,000.00'}
            onChange={this.onChange.bind(this, 'original_loan_amount')}
          />
          <FormCell hintText={'** Instrument No. or Book/Page is required'}>
            <Grid columnCount={2}>
              <TextInput
                label={'Instrument No. **'}
                isRequired={this.props.requiredFields.instrument_number}
                errorText={this.props.errorRecordedMortgage.instrument_number}
                placeholder={'012345'}
                value={this.props.data.instrument_number}
                onChange={this.onChange.bind(this, 'instrument_number')}
              />

              <TextInput
                label={'Book/Page **'}
                placeholder={'012345'}
                isRequired={this.props.requiredFields.book}
                errorText={this.props.errorRecordedMortgage.book}
                value={this.props.data.book}
                onChange={this.onChange.bind(this, 'book')}
              />
            </Grid>
          </FormCell>
        </Grid>
        <Grid columnCount={4}>
          <DateInput
            label={'Execution date'}
            maxDate={DateTime.local().toISODate()}
            isRequired={this.props.requiredFields.execution_date}
            errorText={this.props.errorRecordedMortgage.execution_date}
            value={this.props.data.execution_date}
            onChange={this.onDateChange.bind(this, 'execution_date')}
          />
          <DateInput
            label={'Recorded date'}
            maxDate={DateTime.local().toISODate()}
            minDate={this.props.data.execution_date ? this.props.data.execution_date : undefined}
            isRequired={this.props.requiredFields.recorded_date}
            errorText={this.props.errorRecordedMortgage.recorded_date}
            value={this.props.data.recorded_date}
            onChange={this.onDateChange.bind(this, 'recorded_date')}
          />
          <TimeInput
            label={'Recorded time'}
            isRequired={this.props.requiredFields.recorded_at}
            errorText={this.props.errorRecordedMortgage.recorded_at}
            inputValue={this.props.data.recorded_at}
            onChange={this.onChange.bind(this, 'recorded_at')}
          />
          <DateInput
            label={'Maturity date'}
            isRequired={this.props.requiredFields.maturity_date}
            errorText={this.props.errorRecordedMortgage.maturity_date}
            value={this.props.data.maturity_date}
            onChange={this.onDateChange.bind(this, 'maturity_date')}
          />
        </Grid>
      </>
    );
  }

  private onSelect = (propName: string, value: ISelectOption) => {
    this.onChange(propName, value.value, null);
  };

  private onChange = (propName: string, value: any, error?: string | null) => {
    const newValue = {
      ...this.props.data,
      [propName]: value === '' ? null : value,
    };
    this.props.onRecordedMortgageChange(newValue, propName, error);
  };

  private onDateChange = (propName: string, value: IDateInputValue) => {
    const newValue = {
      ...this.props.data,
      [propName]: value.value,
    };
    this.props.onRecordedMortgageChange(newValue, propName, value.error);
  };
}

export default observer(RecordedMortgageEdit);
